/**
 * Configuration Validation Utility
 * 
 * Validates that all required configuration properties are present
 * and have valid values.
 */

import type { AppConfig } from '@/types/config'

interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

type ValidationRule = (value: any) => boolean

class ConfigValidator {
  private requiredFields: (keyof AppConfig)[]
  private validationRules: Record<keyof AppConfig, ValidationRule>

  constructor() {
    this.requiredFields = [
      'API_BASE_URL',
      'API_TIMEOUT', 
      'ENABLE_DEVTOOLS',
      'ENVIRONMENT',
      'VERSION'
    ]

    this.validationRules = {
      API_BASE_URL: (value: any) => typeof value === 'string' && value.length > 0,
      API_TIMEOUT: (value: any) => typeof value === 'number' && value > 0,
      ENABLE_DEVTOOLS: (value: any) => typeof value === 'boolean',
      ENVIRONMENT: (value: any) => typeof value === 'string' && ['development', 'staging', 'production'].includes(value),
      VERSION: (value: any) => typeof value === 'string' && value.length > 0,
      BUILD_DATE: (value: any) => typeof value === 'string' && value.length > 0,
      FEATURES: (value: any) => typeof value === 'object' && value !== null,
      OAUTH: (value: any) => typeof value === 'object' && value !== null,
      STORAGE: (value: any) => typeof value === 'object' && value !== null,
      I18N: (value: any) => typeof value === 'object' && value !== null
    }
  }

  /**
   * Validates a configuration object
   * @param config - Configuration object to validate
   * @returns Validation result with isValid and errors
   */
  validate(config: Partial<AppConfig> | null | undefined): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    if (!config || typeof config !== 'object') {
      return {
        isValid: false,
        errors: ['Configuration must be a valid object'],
        warnings: []
      }
    }

    // Check for missing required fields
    for (const field of this.requiredFields) {
      if (!(field in config)) {
        errors.push(`Missing required field: ${field}`)
        continue
      }

      // Validate field value
      const validator = this.validationRules[field]
      if (validator && !validator(config[field])) {
        errors.push(`Invalid value for ${field}: ${config[field]}`)
      }
    }

    // Check for unexpected fields (warnings only)
    for (const field in config) {
      if (!Object.keys(this.validationRules).includes(field)) {
        warnings.push(`Unexpected field: ${field}`)
      }
    }

    // Environment-specific validations
    const oauthConfig = config.OAUTH
    if (oauthConfig && typeof oauthConfig === 'object') {
      if (oauthConfig.redirectUri && !oauthConfig.redirectUri.startsWith('/')) {
        warnings.push('OAUTH.redirectUri should be a relative path starting with /')
      }

      if (oauthConfig.redirectUri && oauthConfig.redirectUri.includes('://')) {
        warnings.push('OAUTH.redirectUri should be a relative path, not an absolute URL')
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }

  /**
   * Validates and reports configuration
   * @param config - Configuration object to validate  
   * @param throwOnError - Whether to throw on validation errors
   * @returns True if valid
   */
  validateAndReport(config: Partial<AppConfig> | null | undefined, throwOnError = false): boolean {
    const result = this.validate(config)

    if (result.warnings.length > 0) {
      console.warn('⚠️ Configuration warnings:')
      result.warnings.forEach(warning => console.warn(`  - ${warning}`))
    }

    if (!result.isValid) {
      console.error('❌ Configuration validation failed:')
      result.errors.forEach(error => console.error(`  - ${error}`))
      
      if (throwOnError) {
        throw new Error(`Configuration validation failed: ${result.errors.join(', ')}`)
      }
      return false
    }

    console.log('✅ Configuration validation passed')
    return true
  }

  /**
   * Creates a default configuration object
   * @returns Default configuration
   */
  getDefaultConfig(): AppConfig {
    return {
      API_BASE_URL: 'http://localhost:8080',
      API_TIMEOUT: 30000,
      ENABLE_DEVTOOLS: false,
      ENVIRONMENT: 'development',
      VERSION: '1.0.0',
      BUILD_DATE: new Date().toISOString(),
      FEATURES: {
        enableAnalytics: false,
        enableReporting: true,
        enableFileUpload: true,
        enableRealTimeUpdates: false,
        enableAdvancedSearch: true,
        enableExport: true,
        enableImport: true,
        betaFeatures: false
      },
      OAUTH: {
        clientId: 'browser',
        redirectUri: '/callback',
        scope: ['openid', 'offline_access'],
        responseType: 'code',
        flowType: 'authorization_code'
      },
      STORAGE: {
        prefix: 'archiweb_',
        enableEncryption: false,
        defaultExpiry: 86400000, // 24 hours
        maxSize: 5242880 // 5MB
      },
      I18N: {
        defaultLocale: 'en',
        supportedLocales: ['en', 'fr', 'es'],
        fallbackLocale: 'en',
        loadPath: '/locales/{{lng}}.json'
      }
    }
  }
}

export default new ConfigValidator()
